/**
 * Types et interfaces pour le nouveau système d'onboarding en 5 étapes
 */

export type OnboardingStep = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

export type HookResponse = '1_to_3_times' | '5_to_10_times' | "more_than_10_times";

export type ChildGender = 'boy' | 'girl' | 'other';

export interface ChildTemplate {
  id: string;
  name: string;
  gender: ChildGender;
  birthYear: number;
  age: number;
  color?: string; // Couleur de l'enfant (optionnel)
  avatarAnimalId?: string; // ID de l'animal avatar (dog, fox, etc.) (optionnel)
  tasks: SelectedTaskTemplate[];
  rewards: SelectedRewardTemplate[];
  isNew?: boolean; // Flag temporaire pour les animations
}

export interface ParentTemplate {
  name: string;
  email?: string;
  emailVerified?: boolean;
  sendVerificationEmail?: boolean;
  googleSignIn?: boolean;
}

export interface SelectedTaskTemplate {
  id: string;
  title: string;
  description: string;
  titleKey?: string;
  descriptionKey?: string;
  category: string;
  icon: string;
  minAge: number;
  maxAge: number;
  gender?: ChildGender;
  priority: number;
  assignedChildIds?: string[]; // NEW: IDs of children this task is assigned to
}

export interface SelectedRewardTemplate {
  id: string;
  title: string;
  description: string;
  titleKey?: string;
  descriptionKey?: string;
  category: string;
  icon: string;
  minAge: number;
  maxAge: number;
  gender?: ChildGender;
  priority: number;
  assignedChildIds?: string[]; // NEW: IDs of children this reward is assigned to
}

export interface PreviewValidation {
  childId: string;
  taskId: string;
  validatedAt: string;
  points: number;
}

export interface OnboardingData {
  step: OnboardingStep;
  hookResponse: HookResponse | null;
  parent: ParentTemplate;
  children: ChildTemplate[];
  childrenTemplates: ChildTemplate[];
  parentTemplate: ParentTemplate;
  tasksTemplates: TaskTemplate[];
  rewardsTemplates: RewardTemplate[];
  previewValidations?: PreviewValidation[];
  previewPoints?: Record<string, number>; // childId -> points
  startTime: Date;
  stepTimes: Record<OnboardingStep, { start: Date; end?: Date }>;
  modifications: {
    tasksModified: boolean;
    rewardsModified: boolean;
    childrenModified: boolean;
  };
}

export interface TaskTemplate {
  id: string;
  title: string;
  description: string;
  titleKey?: string;
  descriptionKey?: string;
  category: 'wellness' | 'house' | 'values' | 'learning' | 'sports' | 'morning' | 'evening';
  icon: string;
  minAge: number;
  maxAge: number;
  gender?: ChildGender; // undefined = neutre
  priority: number; // pour présélection
  assignedChildIds?: string[]; // IDs of children this task is assigned to
  points?: number; // Points attribués pour cette tâche (défaut: 1)
  frequency?: 'daily' | 'weekly' | 'custom'; // Fréquence de la tâche (défaut: 'daily')
  assignedTo?: string[]; // IDs des enfants assignés (alias pour assignedChildIds)
}

export interface RewardTemplate {
  id: string;
  title: string;
  description: string;
  titleKey?: string;
  descriptionKey?: string;
  category: 'activity' | 'object' | 'privilege' | 'digital' | 'argent' | 'outing' | 'cuisine' | 'experience';
  icon: string;
  minAge: number;
  maxAge: number;
  gender?: ChildGender;
  priority: number;
  assignedChildIds?: string[]; // IDs of children this reward is assigned to
}

export interface AgeGroup {
  key: string;
  label: string;
  minAge: number;
  maxAge: number;
}

export interface OnboardingValidation {
  isValid: boolean;
  errors: string[];
  step: OnboardingStep;
}

export interface OnboardingAnalytics {
  sessionId: string;
  userId?: string;
  startTime: Date;
  currentStep: OnboardingStep;
  stepTimes: Record<OnboardingStep, number>; // durée en ms
  abandonedAt?: OnboardingStep;
  completed: boolean;
  childrenCount: number;
  tasksModified: boolean;
  rewardsModified: boolean;
  configuredLater: boolean;
  hookResponse: HookResponse | null;
}

/** Un enfant tel que saisi en local pendant l'onboarding (aucune dépendance backend). */
export interface OnboardingDraftChild {
  name: string
  gender: 'boy' | 'girl' | 'other'
  birthYear: number
  color?: string
  avatarAnimalId?: string
}

/** Une tâche saisie en local ; assignée par NOM d'enfant (pas d'id avant le commit). */
export interface OnboardingDraftTask {
  title: string
  description?: string
  points: number
  frequency: 'daily' | 'weekly' | 'custom'
  /** Icône Lucide affichée dans le builder (locale, ignorée par le commit). */
  icon?: string
  /** noms des enfants concernés ; vide ou absent = tous. */
  assignedChildNames?: string[]
  /** Id du modèle d'origine (catalog_templates.id) ; absent = tâche écrite par le parent. */
  templateId?: string
}

/** Une récompense saisie en local (optionnelle en v1). */
export interface OnboardingDraftReward {
  title: string
  description?: string
  requiredPoints: number
  category: string
  icon?: string
  isRecurring?: boolean
  /** noms des enfants concernés ; vide ou absent = tous. */
  assignedChildNames?: string[]
  /** Id du modèle d'origine (catalog_templates.id) ; absent = récompense écrite par le parent. */
  templateId?: string
}

/** Brouillon complet d'onboarding, persisté localement via Preferences. */
export interface OnboardingDraft {
  version: number
  parentName: string
  /**
   * Le « Vous êtes » du premier écran (mom, dad, step-mom, ...).
   *
   * Facultatif : un brouillon écrit avant ce champ n'en porte pas, et sa
   * reprise ne doit pas échouer pour autant. La liste fait autorité côté base
   * (contrainte `household_members_parent_type_check`), qui ignore toute
   * valeur inconnue plutôt que de faire échouer la création du foyer.
   */
  parentType?: string | null
  /**
   * Réponse à « Combien d'enfants ? » de l'écran enfants (HARMONIA-94), 4 pour
   * « 4 ou plus ». Mesure seulement : on ne crée toujours qu'un enfant.
   * Facultatif pour la même raison que `parentType`.
   */
  declaredChildrenCount?: number | null
  locale: string
  children: OnboardingDraftChild[]
  tasks: OnboardingDraftTask[]
  rewards: OnboardingDraftReward[]
  createdAt: string
  /** uuid posé à la 1re tentative de commit (idempotence). null avant. */
  commitId: string | null
  /** posé après commit réussi côté serveur ; déclenche purge sans recommit. null avant. */
  committedAt: string | null
}

export interface CommitResult {
  householdId: string
  childrenIds: string[]
  status: 'committed' | 'already_committed'
}
/** Étape d'onboarding qu'un brouillon incomplet doit (re)faire avant le commit. */
export type MissingDraftStep = 'name' | 'children' | 'tasks' | 'rewards'

/**
 * Valide la complétude d'un brouillon d'onboarding avant le commit serveur.
 * Retourne la PREMIÈRE étape manquante (dans l'ordre du parcours) ou null si
 * le brouillon est complet. Un onboarding complet exige : prénom parent,
 * ≥1 enfant, ≥1 tâche, ≥1 récompense. Évite de committer un foyer partiel
 * (ex. reprise de parcours ayant sauté une étape).
 */
/**
 * Âges (en années) des enfants du brouillon, calculés depuis birthYear.
 * Les enfants sans année de naissance connue (birthYear 0) sont ignorés.
 */
export function childAgesFromDraft(draft: OnboardingDraft | null): number[] {
  if (!draft) return []
  const currentYear = new Date().getFullYear()
  return draft.children
    .filter(c => c.birthYear > 0)
    .map(c => currentYear - c.birthYear)
}

export function firstMissingDraftStep(draft: OnboardingDraft | null): MissingDraftStep | null {
  if (!draft || !draft.parentName || draft.parentName.trim() === '') return 'name'
  if (!draft.children || draft.children.length === 0) return 'children'
  if (!draft.tasks || draft.tasks.length === 0) return 'tasks'
  if (!draft.rewards || draft.rewards.length === 0) return 'rewards'
  return null
}
